import React, { memo, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { Icon } from '../../../components/common'
import { constants } from '../../../constants'
import { LanguageContext } from '../../../context/LanguageContext'
import { util } from '../../../util'

const SideDrawer = ({ toggleSideMenu }) => {
    const { strings } = useContext(LanguageContext)

    const [menu, setMenu] = useState({})
    const adp_configurations = useSelector(({ common }) => common.adp_configurations)

    const user_permissions = useSelector(({ auth }) => auth.user_permissions)

    const isUserPermitted = permission => {
        return user_permissions.includes(permission)
    }

    const initialMenuList = [
        {
            configFeatureKey: constants.configFeatureFlags.OPS,
            icon: util.StoreOpsToolsIcon,
            label: strings.BREADCRUMB_OPS_TOOLS,
            subMenu: [
                {
                    label: strings.BREADCRUMB_STORES,
                    link: constants.routePaths.stores.path,
                    show: isUserPermitted(constants.routePaths.stores.permission),
                },
                {
                    label: strings.BREADCRUMB_ORDERS,
                    link: constants.routePaths.orders.path,
                    show: isUserPermitted(constants.routePaths.orders.permission),
                },
            ],
        },
        {
            configFeatureKey: constants.configFeatureFlags.PROMOTIONS,
            icon: util.PromotionsIcon,
            label: strings.BREADCRUMB_PROMOTIONS,
            subMenu: [
                {
                    label: strings.BREADCRUMB_MANAGE_PROMOS,
                    link: constants.routePaths.promotions.path,
                    show: isUserPermitted(constants.routePaths.promotions.permission),
                },
                {
                    label: strings.BREADCRUMB_PRIORITIZE_PROMOS,
                    link: constants.routePaths.prioritize_promos.path,
                    show: isUserPermitted(constants.routePaths.prioritize_promos.permission),
                },
            ],
        },
        {
            configFeatureKey: constants.configFeatureFlags.PUSH_NOTIFICATIONS,
            icon: util.PushNotificationsIcon,
            label: strings.BREADCRUMB_PUSH_NOTIFICATIONS,
            subMenu: [
                {
                    label: strings.BREADCRUMB_MANAGE_PUSH_NOTIFICATIONS,
                    link: constants.routePaths.push_notifications.path,
                    show: isUserPermitted(constants.routePaths.push_notifications.permission),
                },
            ],
        },
        {
            configFeatureKey: constants.configFeatureFlags.MANAGEMENT_DATA,
            icon: util.StoreIcon2,
            label: strings.BREADCRUMB_DATA_MANAGEMENT,
            subMenu: [
                {
                    label: strings.BREADCRUMB_MANAGE_STORES_IMPORTS,
                    link: constants.routePaths.manage_stores_imports.path,
                    show: isUserPermitted(constants.routePaths.manage_stores_imports.permission),
                },
                {
                    label: strings.BREADCRUMB_MANAGE_PRODUCTS_IMPORTS,
                    link: constants.routePaths.manage_products_imports.path,
                    show: isUserPermitted(constants.routePaths.manage_products_imports.permission),
                },
                {
                    label: strings.BREADCRUMB_MANAGE_INVENTORY_IMPORTS,
                    link: constants.routePaths.manage_inventory_imports.path,
                    show: isUserPermitted(constants.routePaths.manage_inventory_imports.permission),
                },
                {
                    label: strings.BREADCRUMB_MANAGE_NUTRITION_IMPORTS,
                    link: constants.routePaths.manage_nutrition_imports.path,
                    show: isUserPermitted(constants.routePaths.manage_inventory_imports.permission),
                },
            ],
        },
        {
            configFeatureKey: constants.configFeatureFlags.ASSORTMENT,
            icon: util.AssortmentIcon,
            label: strings.BREADCRUMB_ASSORTMENT,
            subMenu: [
                {
                    label: strings.BREADCRUMB_PRODUCTS,
                    link: constants.routePaths.products.path,
                    show: isUserPermitted(constants.routePaths.products.permission),
                },
                {
                    label: strings.BREADCRUMB_MANAGE_UPDATES,
                    link: constants.routePaths.manage_updates.path,
                    show: isUserPermitted(constants.routePaths.manage_updates.permission),
                },
                {
                    label: strings.BREADCRUMB_ADD_NEW_PRODUCTS,
                    link: constants.routePaths.add_new_products.path,
                    show: isUserPermitted(constants.routePaths.add_new_products.permission),
                },
            ],
        },
    ]

    const [sideDrawerList, setSideMenuList] = useState(initialMenuList)

    useEffect(() => {
        if (!adp_configurations || !adp_configurations.feature_flag) {
            setSideMenuList(initialMenuList)
            return
        }

        const activeFeatureFlags = adp_configurations.feature_flag.reduce(
            (acc, { key, is_active }) => {
                acc[key] = is_active
                return acc
            },
            {}
        )

        const updatedSideMenuList = sideDrawerList.map(menu => {
            if (!menu.configFeatureKey) {
                return menu
            }
            return { ...menu, isActive: activeFeatureFlags[menu.configFeatureKey] }
        })
        setSideMenuList(updatedSideMenuList)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adp_configurations])

    const clickHandler = item =>
        setMenu(prevState => {
            return { [item]: !prevState[item] }
        })

    const renderMainMenu = ({ icon, label, link }) => (
        <Link className={'side-drawer__link'} to={link} key={label}>
            <div className={'side-drawer__item'} onClick={toggleSideMenu}>
                {icon && <Icon className={'side-drawer-icon'} renderIcon={icon} />}
                <div className={'side-drawer__item__main'}>{label}</div>
            </div>
        </Link>
    )

    const renderSubMenu = ({ icon, label, subMenu }) => {
        return (
            <div key={label}>
                {subMenu.some(sm => sm.show) && (
                    <div
                        className={util.joinClasses('side-drawer__item', menu[label] && 'selected')}
                        onClick={() => clickHandler(label)}
                    >
                        {icon && <Icon className={'side-drawer-icon'} renderIcon={icon} />}
                        <div
                            className={util.joinClasses(
                                'side-drawer__item__main',
                                menu[label] && 'selected'
                            )}
                        >
                            {label}
                        </div>
                        <Icon
                            className={'side-drawer__item__icon-indicator'}
                            size={'md'}
                            renderIcon={menu[label] ? util.ExpandLessIcon : util.ExpandMoreIcon}
                        />
                        {menu[label] && (
                            <div
                                onClick={e => e.stopPropagation()}
                                className={'side-drawer__item__sub-menu'}
                            >
                                {renderSideDrawer(subMenu)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    const renderSideDrawer = menuList =>
        menuList.map(menu => {
            if (menu.hasOwnProperty('configFeatureKey')) {
                if (!menu.isActive) {
                    return null
                }
            }

            const { icon, label, link, subMenu, show } = menu
            return !subMenu
                ? show !== false && renderMainMenu({ icon, label, link })
                : renderSubMenu({ icon, label, subMenu })
        })

    return <div className={'side-drawer'}>{renderSideDrawer(sideDrawerList)}</div>
}

export default memo(SideDrawer)
